import { useEffect, useState } from "react";
import { ChevronUp, ChevronDown, Eye, EyeOff, Pencil, Trash2, Check, X, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useMeta } from "@/lib/useMeta";
import AdminGate, { AdminNav } from "@/components/AdminGate";
import { refreshTaxonomies } from "@/lib/taxonomies";
import {
  fetchTaxonomiesAdmin,
  createTaxonomy,
  updateTaxonomy,
  deleteTaxonomy,
  createTerm,
  updateTerm,
  deleteTerm,
  moveItems,
  nextSort,
  adminErrorMessage,
  KEY_PATTERN,
  VALUE_PATTERN,
} from "@/lib/adminTaxonomies";

function IconButton({ label, onClick, disabled, children, danger }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={`h-8 w-8 ${danger ? "text-destructive" : ""}`}
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function Flag({ checked, onChange, children }) {
  return (
    <label className="flex items-center gap-1.5 text-sm text-foreground">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {children}
    </label>
  );
}

function TermRow({ term, index, list, run, busy }) {
  const [editing, setEditing] = useState(false);
  const [bn, setBn] = useState(term.label_bn);
  const [en, setEn] = useState(term.label_en ?? "");

  if (editing) {
    return (
      <li className="flex flex-wrap items-center gap-2 py-2">
        <Input className="h-8 w-40" value={bn} onChange={(e) => setBn(e.target.value)} placeholder="বাংলা নাম" />
        <Input className="h-8 w-40" value={en} onChange={(e) => setEn(e.target.value)} placeholder="English (ঐচ্ছিক)" />
        <IconButton
          label="সেভ"
          disabled={busy || !bn.trim()}
          onClick={() => run(async () => { await updateTerm(term.id, { label_bn: bn.trim(), label_en: en.trim() || null }); setEditing(false); })}
        >
          <Check className="w-4 h-4" />
        </IconButton>
        <IconButton label="বাতিল" onClick={() => { setBn(term.label_bn); setEn(term.label_en ?? ""); setEditing(false); }}>
          <X className="w-4 h-4" />
        </IconButton>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-1 py-1.5">
      <div className="min-w-0 flex-1">
        <span className={term.active ? "text-foreground" : "text-muted-foreground line-through"}>{term.label_bn}</span>
        {term.label_en && <span className="text-xs text-muted-foreground"> · {term.label_en}</span>}
        <code className="ml-2 text-[11px] text-muted-foreground">{term.value}</code>
      </div>
      <IconButton label="উপরে" disabled={busy || index === 0} onClick={() => run(() => moveItems("terms", list, index, -1))}>
        <ChevronUp className="w-4 h-4" />
      </IconButton>
      <IconButton label="নিচে" disabled={busy || index === list.length - 1} onClick={() => run(() => moveItems("terms", list, index, 1))}>
        <ChevronDown className="w-4 h-4" />
      </IconButton>
      <IconButton label={term.active ? "নিষ্ক্রিয় করো" : "সক্রিয় করো"} disabled={busy} onClick={() => run(() => updateTerm(term.id, { active: !term.active }))}>
        {term.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </IconButton>
      <IconButton label="নাম বদলাও" disabled={busy} onClick={() => setEditing(true)}>
        <Pencil className="w-4 h-4" />
      </IconButton>
      <IconButton
        label="মুছো"
        danger
        disabled={busy}
        onClick={() => window.confirm(`"${term.label_bn}" মুছবে? (পেপারে ব্যবহার হলে মোছা যাবে না)`) && run(() => deleteTerm(term.id))}
      >
        <Trash2 className="w-4 h-4" />
      </IconButton>
    </li>
  );
}

function AddTerm({ tax, run, busy }) {
  const [value, setValue] = useState("");
  const [bn, setBn] = useState("");
  const [en, setEn] = useState("");
  const valid = VALUE_PATTERN.test(value) && bn.trim().length > 0;

  const add = () =>
    run(async () => {
      await createTerm(tax.id, { value, label_bn: bn.trim(), label_en: en.trim() || null, sort: nextSort(tax.terms) });
      setValue("");
      setBn("");
      setEn("");
    });

  return (
    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border">
      <Input className="h-8 w-32 font-mono text-xs" value={value} onChange={(e) => setValue(e.target.value.trim())} placeholder="value (class-11)" />
      <Input className="h-8 w-40" value={bn} onChange={(e) => setBn(e.target.value)} placeholder="বাংলা নাম" />
      <Input className="h-8 w-40" value={en} onChange={(e) => setEn(e.target.value)} placeholder="English (ঐচ্ছিক)" />
      <Button size="sm" disabled={busy || !valid} onClick={add}>
        <Plus className="w-4 h-4 mr-1" /> মান যোগ
      </Button>
      {value && !VALUE_PATTERN.test(value) && <p className="w-full text-xs text-destructive">value: ছোট ইংরেজি অক্ষর, সংখ্যা বা - (সর্বোচ্চ ৪০)। পরে বদলানো যায় না।</p>}
    </div>
  );
}

function TaxonomyCard({ tax, index, list, run, busy }) {
  const [editing, setEditing] = useState(false);
  const [bn, setBn] = useState(tax.label_bn);
  const [en, setEn] = useState(tax.label_en ?? "");
  const [multi, setMulti] = useState(tax.multi);
  const [required, setRequired] = useState(tax.required);

  return (
    <section className={`paper-card p-4 ${tax.active ? "" : "opacity-70"}`}>
      <div className="flex items-start gap-1">
        <div className="min-w-0 flex-1">
          <h2 className="font-display font-semibold text-foreground">
            {tax.label_bn} <code className="text-xs font-normal text-muted-foreground">{tax.key}</code>
          </h2>
          <div className="flex flex-wrap gap-1.5 mt-1">
            <Badge variant="outline">{tax.multi ? "একাধিক বাছা যায়" : "একটাই বাছা যায়"}</Badge>
            {tax.required && <Badge variant="secondary">পাবলিশে বাধ্যতামূলক</Badge>}
            {!tax.active && <Badge variant="destructive">নিষ্ক্রিয়</Badge>}
          </div>
        </div>
        <IconButton label="উপরে" disabled={busy || index === 0} onClick={() => run(() => moveItems("taxonomies", list, index, -1))}>
          <ChevronUp className="w-4 h-4" />
        </IconButton>
        <IconButton label="নিচে" disabled={busy || index === list.length - 1} onClick={() => run(() => moveItems("taxonomies", list, index, 1))}>
          <ChevronDown className="w-4 h-4" />
        </IconButton>
        <IconButton label={tax.active ? "নিষ্ক্রিয় করো" : "সক্রিয় করো"} disabled={busy} onClick={() => run(() => updateTaxonomy(tax.id, { active: !tax.active }))}>
          {tax.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </IconButton>
        <IconButton label="সম্পাদনা" disabled={busy} onClick={() => setEditing((v) => !v)}>
          <Pencil className="w-4 h-4" />
        </IconButton>
        <IconButton
          label="মুছো"
          danger
          disabled={busy}
          onClick={() => window.confirm(`"${tax.label_bn}" ক্যাটাগরি ও তার সব মান মুছবে? (ব্যবহার হলে বা মূল ক্যাটাগরি হলে মোছা যাবে না)`) && run(() => deleteTaxonomy(tax.id))}
        >
          <Trash2 className="w-4 h-4" />
        </IconButton>
      </div>

      {editing && (
        <div className="flex flex-wrap items-center gap-3 mt-3 pb-3 border-b border-border">
          <Input className="h-8 w-40" value={bn} onChange={(e) => setBn(e.target.value)} placeholder="বাংলা নাম" />
          <Input className="h-8 w-40" value={en} onChange={(e) => setEn(e.target.value)} placeholder="English (ঐচ্ছিক)" />
          <Flag checked={multi} onChange={setMulti}>একাধিক বাছা যাবে</Flag>
          <Flag checked={required} onChange={setRequired}>পাবলিশে বাধ্যতামূলক</Flag>
          <Button
            size="sm"
            disabled={busy || !bn.trim()}
            onClick={() => run(async () => { await updateTaxonomy(tax.id, { label_bn: bn.trim(), label_en: en.trim() || null, multi, required }); setEditing(false); })}
          >
            সেভ
          </Button>
        </div>
      )}

      <ul className="divide-y divide-border mt-2">
        {tax.terms.map((term, i) => (
          <TermRow key={term.id} term={term} index={i} list={tax.terms} run={run} busy={busy} />
        ))}
        {tax.terms.length === 0 && <li className="py-2 text-sm text-muted-foreground">এখনো কোনো মান নেই।</li>}
      </ul>
      <AddTerm tax={tax} run={run} busy={busy} />
    </section>
  );
}

function NewTaxonomy({ list, run, busy }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [bn, setBn] = useState("");
  const [en, setEn] = useState("");
  const [multi, setMulti] = useState(false);
  const [required, setRequired] = useState(false);
  const valid = KEY_PATTERN.test(key) && bn.trim().length > 0;

  if (!open) {
    return (
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Plus className="w-4 h-4 mr-1" /> নতুন ক্যাটাগরি
      </Button>
    );
  }
  return (
    <section className="paper-card p-4 space-y-3">
      <h2 className="font-display font-semibold text-foreground">নতুন ক্যাটাগরি</h2>
      <div className="flex flex-wrap items-center gap-2">
        <Input className="h-8 w-40 font-mono text-xs" value={key} onChange={(e) => setKey(e.target.value.trim())} placeholder="key (board)" />
        <Input className="h-8 w-40" value={bn} onChange={(e) => setBn(e.target.value)} placeholder="বাংলা নাম (বোর্ড)" />
        <Input className="h-8 w-40" value={en} onChange={(e) => setEn(e.target.value)} placeholder="English (ঐচ্ছিক)" />
      </div>
      {key && !KEY_PATTERN.test(key) && <p className="text-xs text-destructive">key: ছোট ইংরেজি অক্ষর দিয়ে শুরু; অক্ষর, সংখ্যা বা _ (২–৩০)। পরে বদলানো যায় না।</p>}
      <div className="flex flex-wrap items-center gap-4">
        <Flag checked={multi} onChange={setMulti}>একাধিক বাছা যাবে</Flag>
        <Flag checked={required} onChange={setRequired}>পাবলিশে বাধ্যতামূলক</Flag>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={busy || !valid}
          onClick={() =>
            run(async () => {
              await createTaxonomy({ key, label_bn: bn.trim(), label_en: en.trim() || null, multi, required, sort: nextSort(list) });
              setOpen(false);
              setKey("");
              setBn("");
              setEn("");
              setMulti(false);
              setRequired(false);
            })
          }
        >
          তৈরি করো
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          বাতিল
        </Button>
      </div>
    </section>
  );
}

function Categories({ isAdmin }) {
  useMeta({ title: "ক্যাটাগরি — অ্যাডমিন — মুমতাহিন গ্যালারি" });
  const [taxonomies, setTaxonomies] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0); // বাড়লে তালিকা নতুন করে আনা হয়

  useEffect(() => {
    let active = true;
    fetchTaxonomiesAdmin()
      .then((data) => {
        if (!active) return;
        setTaxonomies(data);
        setFailed(false);
      })
      .catch((err) => {
        console.error("Taxonomies load failed:", err);
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setBusy(false);
        refreshTaxonomies({ force: true }); // সাইটের বাকি অংশ ও ক্যাশও নতুন হয়ে যায়
      });
    return () => {
      active = false;
    };
  }, [version]);

  // সব পরিবর্তন এখান দিয়ে: চলার সময় বাটন বন্ধ (তালিকা নতুন করে আসা পর্যন্ত), ব্যর্থ হলে বার্তা
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      setVersion((v) => v + 1);
    } catch (err) {
      console.error("Category action failed:", err);
      alert(adminErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div className="container py-8 max-w-3xl">
      <AdminNav isAdmin={isAdmin} />
      <p className="text-sm text-muted-foreground mb-5">
        পেপারে ট্যাগ হিসেবে যা বাছা যায় (শ্রেণি, বিষয়, প্রশ্নের ধরন…)। এখানে বদলালে কোড ডিপ্লয় লাগে না; সাইটে সর্বোচ্চ ১ ঘণ্টার মধ্যে নতুন তালিকা দেখা যায়।
        নিষ্ক্রিয় করা মান পুরনো পেপারে থাকে, কিন্তু নতুন পেপারে বাছা যায় না।
      </p>

      {failed ? (
        <p className="text-sm text-muted-foreground">লোড করা যাচ্ছে না। migration 0005 রান করা হয়েছে তো?</p>
      ) : taxonomies === null ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-4">
          {taxonomies.map((tax, i) => (
            <TaxonomyCard key={tax.id} tax={tax} index={i} list={taxonomies} run={run} busy={busy} />
          ))}
          <NewTaxonomy list={taxonomies} run={run} busy={busy} />
        </div>
      )}
    </div>
  );
}

export default function AdminCategories() {
  return <AdminGate adminOnly>{({ isAdmin }) => <Categories isAdmin={isAdmin} />}</AdminGate>;
}
